/**
 * Limits matrix.  Key format: `${tier}` for free, `${tier}:${mode}` for pro.
 * Using Infinity for "unlimited" so numeric comparison always passes.
 *
 * Commercial submit limits (`applicationsPerDay`) are unchanged from the prior
 * shared bag; letters now have their own `coverLettersPerDay` with matching
 * starting numbers so migration is additive, not a silent cut. Mock interview
 * sessions start with the same numbers as the guides (D4), in their own bag.
 */
const LIMITS = {
    free: {
        scansPerDay: 1,
        scansPerWeek: Infinity, // daily cap applies instead
        scansPerMonth: Infinity,
        evalsPerScan: 50,
        evalsPerDay: 50,
        applicationsPerDay: 3,
        coverLettersPerDay: 3,
        prepsPerWeek: 3,
        prepsPerMonth: Infinity,
        mockSessionsPerWeek: 3,
        mockSessionsPerMonth: Infinity,
        assistantMessagesPerDay: 20,
    },
    "pro:search": {
        scansPerDay: 2,
        scansPerWeek: Infinity,
        scansPerMonth: Infinity,
        evalsPerScan: 300,
        evalsPerDay: 300,
        applicationsPerDay: Infinity,
        coverLettersPerDay: Infinity,
        prepsPerWeek: Infinity,
        prepsPerMonth: Infinity,
        mockSessionsPerWeek: Infinity,
        mockSessionsPerMonth: Infinity,
        assistantMessagesPerDay: 200,
    },
    "pro:radar": {
        scansPerDay: Infinity, // radar scans are automatic, capped by scansPerMonth
        scansPerWeek: Infinity,
        scansPerMonth: 4,
        evalsPerScan: 50,
        evalsPerDay: 50,
        applicationsPerDay: 2,
        coverLettersPerDay: 2,
        prepsPerWeek: Infinity,
        prepsPerMonth: 5,
        mockSessionsPerWeek: Infinity,
        mockSessionsPerMonth: 5,
        assistantMessagesPerDay: 50,
    },
};
export function getPlanLimits(tier, mode) {
    if (tier === "free")
        return LIMITS.free;
    return LIMITS[`pro:${mode}`] ?? LIMITS["pro:search"];
}
/**
 * Every `PlanLimits` field, at runtime. The predeploy checks read it from the
 * shared build (`scripts/lib/spec-inventory.mjs`, `pricing-quota-map.mjs`),
 * where knip cannot follow it.
 * @public
 */
export const PLAN_LIMIT_KEYS = Object.keys(LIMITS.free);
/**
 * Offers one search run analyzes in free-for-all mode, whatever the plan: the
 * scan engine's cap (`getEffectiveLimits` in functions agent/engine.ts) and
 * the size the inbox announces for «Analizar» (`evalRunSize` in web). The
 * one number both read.
 */
export const FREE_FOR_ALL_EVALS_PER_SCAN = 300;
/**
 * How long one mock interview session lasts (D4): the first scored answer of
 * a guide opens it and charges `mock_interview` once; later answers to that
 * guide's questions within the window are free.
 */
export const MOCK_INTERVIEW_SESSION_HOURS = 24;
// ---------------------------------------------------------------------------
// Plan prices (USD/month) — single source of truth for backend MRR math
// ---------------------------------------------------------------------------
export const PLAN_PRICES_USD = {
    search: 11.99,
    radar: 3.99,
};
/**
 * Monthly saving when a PRO subscriber moves from search to radar. Derived so
 * copy never hand-writes an amount that drifts from `PLAN_PRICES_USD`.
 */
export const RADAR_MONTHLY_SAVINGS_USD = Math.round((PLAN_PRICES_USD.search - PLAN_PRICES_USD.radar) * 100) / 100;
/**
 * Radar is only reachable as a downgrade from PRO (decision D1, specs/12 §3):
 * radar gives fewer sends, letters and scans than Free, so a Free user must
 * never be able to buy it directly. Search is always purchasable.
 */
export function canCheckoutProMode(tier, mode) {
    return mode === "search" || tier === "pro";
}
// ---------------------------------------------------------------------------
// Lemon Squeezy variant identifiers — replace with real variant IDs from
// the Lemon Squeezy dashboard after creating the products.
// ---------------------------------------------------------------------------
export const LEMONSQUEEZY_VARIANTS = {
    prod: {
        proSearch: 1600176,
        proRadar: 1600192,
    },
    test: {
        proSearch: 1526532,
        proRadar: 1526555,
    },
};
// ---------------------------------------------------------------------------
// Gated features — the gate itself (`isFeatureAvailable`) lives next to the
// counters in usage-balance.ts: one rule for client and server (q-shared-04)
// ---------------------------------------------------------------------------
/**
 * Backend triggers call reserveUsage / enforceAndIncrement with these
 * granular names; `featureCounters` maps them to the counters they spend.
 * Which user action spends which feature is `USAGE_ACTION_MATRIX`.
 */
export const GATED_FEATURES = [
    "scan",
    "cv_generation",
    "cover_letter",
    "interview_prep",
    "negotiation_guide",
    "mock_interview",
    "application",
    "radar",
    "eval_match",
    "assistant_chat",
];
//# sourceMappingURL=plan-limits.js.map